import { supabase, isConfigured } from './supabase.js';

// Local storage fallback for guest or unconfigured mode
const LOCAL_USER_KEY = 'wt_discipleship_user';
const LOCAL_PROFILE_KEY = 'wt_discipleship_profile';

export const authState = {
  user: null,
  profile: null,
  listeners: []
};

export function subscribeAuth(callback) {
  authState.listeners.push(callback);
  callback({ user: authState.user, profile: authState.profile });
  return () => {
    authState.listeners = authState.listeners.filter(cb => cb !== callback);
  };
}

function notifyListeners() {
  authState.listeners.forEach(cb => cb({ user: authState.user, profile: authState.profile }));
}

export async function initAuth() {
  if (!isConfigured) {
    const saved = localStorage.getItem(LOCAL_USER_KEY);
    if (saved) {
      try {
        authState.user = JSON.parse(saved);
        authState.profile = JSON.parse(localStorage.getItem(LOCAL_PROFILE_KEY) || '{}');
      } catch (e) {
        authState.user = null;
      }
    }
    notifyListeners();
    return;
  }

  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (session?.user) {
      authState.user = session.user;
      await fetchProfile(session.user.id);
    } else {
      authState.user = null;
      authState.profile = null;
    }
  } catch (err) {
    console.warn('Auth session check failed, using local fallback:', err);
  }

  // Subscribe to changes
  supabase.auth.onAuthStateChange(async (event, session) => {
    if (session?.user) {
      authState.user = session.user;
      await fetchProfile(session.user.id);
    } else {
      authState.user = null;
      authState.profile = null;
    }
    notifyListeners();
  });

  notifyListeners();
}

async function fetchProfile(userId) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (data) {
      authState.profile = data;
    } else {
      // Fallback profile if row does not exist yet
      authState.profile = {
        id: userId,
        display_name: authState.user?.user_metadata?.display_name || authState.user?.email?.split('@')[0] || 'Disciple',
        full_name: authState.user?.user_metadata?.full_name || '',
        cohort_name: authState.user?.user_metadata?.cohort_name || 'General Cohort',
        role: 'student'
      };
    }
  } catch (e) {
    console.warn('Could not fetch profile:', e);
    authState.profile = {
      id: userId,
      display_name: authState.user?.user_metadata?.display_name || 'Disciple',
      full_name: authState.user?.user_metadata?.full_name || '',
      cohort_name: 'General Cohort'
    };
  }
}

export async function signUp({ email, password, fullName, displayName, cohortName, groupCode, newGroupName }) {
  if (!isConfigured) {
    // Offline simulation
    const mockUser = {
      id: 'local-' + Date.now(),
      email,
      user_metadata: { full_name: fullName, display_name: displayName, cohort_name: cohortName || newGroupName || 'General Cohort' }
    };
    const mockProfile = {
      id: mockUser.id,
      full_name: fullName,
      display_name: displayName || email.split('@')[0],
      cohort_name: cohortName || newGroupName || 'General Cohort',
      role: newGroupName ? 'leader' : 'student'
    };
    localStorage.setItem(LOCAL_USER_KEY, JSON.stringify(mockUser));
    localStorage.setItem(LOCAL_PROFILE_KEY, JSON.stringify(mockProfile));
    authState.user = mockUser;
    authState.profile = mockProfile;

    if (groupCode) {
      try { await joinGroup(groupCode); } catch (e) { console.warn(e); }
    } else if (newGroupName) {
      try { await createGroup({ name: newGroupName }); } catch (e) { console.warn(e); }
    }

    notifyListeners();
    return { data: { user: mockUser }, error: null };
  }

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name: fullName,
        display_name: displayName || fullName || email.split('@')[0],
        cohort_name: cohortName || newGroupName || 'General Cohort'
      }
    }
  });

  if (error) throw error;

  // If user signed up with a group code or created a new group, attach immediately
  if (data?.user) {
    authState.user = data.user;
    if (groupCode) {
      try { await joinGroup(groupCode); } catch (e) { console.warn('Could not auto-join group:', e); }
    } else if (newGroupName) {
      try { await createGroup({ name: newGroupName }); } catch (e) { console.warn('Could not auto-create group:', e); }
    }
  }

  return data;
}

export async function signIn({ email, password }) {
  if (!isConfigured) {
    const mockUser = {
      id: 'local-user',
      email,
      user_metadata: { full_name: 'Joe P', display_name: 'Joe P', cohort_name: 'Wells Branch Cohort' }
    };
    const mockProfile = {
      id: mockUser.id,
      full_name: 'Joe P',
      display_name: 'Joe P',
      cohort_name: 'Wells Branch Cohort',
      role: 'student'
    };
    localStorage.setItem(LOCAL_USER_KEY, JSON.stringify(mockUser));
    localStorage.setItem(LOCAL_PROFILE_KEY, JSON.stringify(mockProfile));
    authState.user = mockUser;
    authState.profile = mockProfile;
    notifyListeners();
    return { user: mockUser };
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });

  if (error) throw error;
  return data;
}

export async function signOut() {
  if (!isConfigured) {
    localStorage.removeItem(LOCAL_USER_KEY);
    localStorage.removeItem(LOCAL_PROFILE_KEY);
    authState.user = null;
    authState.profile = null;
    notifyListeners();
    return;
  }

  const { error } = await supabase.auth.signOut();
  if (error) throw error;
  authState.user = null;
  authState.profile = null;
  notifyListeners();
}

export async function resetPassword(email) {
  if (!isConfigured) {
    return { success: true };
  }
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin
  });
  if (error) throw error;
  return data;
}

export async function updateProfile(updates) {
  if (!authState.user) return;
  const userId = authState.user.id;

  if (!isConfigured) {
    authState.profile = { ...authState.profile, ...updates };
    localStorage.setItem(LOCAL_PROFILE_KEY, JSON.stringify(authState.profile));
    notifyListeners();
    return;
  }

  const { data, error } = await supabase
    .from('profiles')
    .upsert({ id: userId, ...updates })
    .select()
    .single();

  if (error) throw error;
  if (data) {
    authState.profile = data;
    notifyListeners();
  }
  return data;
}

// -----------------------------------------------------------------------------
// GROUP / COHORT REGISTRATION FUNCTIONS
// -----------------------------------------------------------------------------

const LOCAL_GROUPS_KEY = 'wt_discipleship_groups';
const LOCAL_CURRENT_GROUP_KEY = 'wt_discipleship_current_group';

export async function lookupGroupByCode(code) {
  if (!code) return null;
  const cleanCode = code.trim().toUpperCase();

  if (!isConfigured) {
    try {
      const groups = JSON.parse(localStorage.getItem(LOCAL_GROUPS_KEY) || '[]');
      return groups.find(g => g.invite_code.toUpperCase() === cleanCode) || null;
    } catch (e) {
      return null;
    }
  }

  const { data, error } = await supabase
    .from('groups')
    .select('id, name, invite_code, description, is_active')
    .ilike('invite_code', cleanCode)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    console.warn('Error looking up group by code:', error);
    return null;
  }
  return data;
}

export async function fetchUserGroup(userId = authState.user?.id) {
  if (!userId) return null;

  if (!isConfigured) {
    try {
      const g = localStorage.getItem(LOCAL_CURRENT_GROUP_KEY);
      return g ? JSON.parse(g) : null;
    } catch (e) {
      return null;
    }
  }

  const { data, error } = await supabase
    .from('group_members')
    .select('role, joined_at, groups (id, name, invite_code, description, leader_id)')
    .eq('user_id', userId)
    .order('joined_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data || !data.groups) return null;

  return {
    ...data.groups,
    my_role: data.role,
    joined_at: data.joined_at
  };
}

export async function createGroup({ name, description = '' }) {
  if (!authState.user) throw new Error('You must be signed in to create a group.');

  if (!isConfigured) {
    // Generate clean code WALK-XXXX
    const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
    let code = 'WALK-';
    for (let i = 0; i < 4; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));

    const newGroup = {
      id: 'group-' + Date.now(),
      name: name.trim(),
      description: description.trim(),
      invite_code: code,
      leader_id: authState.user.id,
      my_role: 'leader',
      created_at: new Date().toISOString()
    };

    const existing = JSON.parse(localStorage.getItem(LOCAL_GROUPS_KEY) || '[]');
    existing.push(newGroup);
    localStorage.setItem(LOCAL_GROUPS_KEY, JSON.stringify(existing));
    localStorage.setItem(LOCAL_CURRENT_GROUP_KEY, JSON.stringify(newGroup));

    // Update profile
    await updateProfile({ cohort_name: newGroup.name });
    return newGroup;
  }

  // Use database function or direct insert
  const { data, error } = await supabase.rpc('create_group_with_leader', {
    group_name: name.trim(),
    group_desc: description.trim()
  });

  if (error) {
    // Fallback direct insert if RPC not run yet
    const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
    let code = 'WALK-';
    for (let i = 0; i < 4; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));

    const { data: directGroup, error: dErr } = await supabase
      .from('groups')
      .insert({
        name: name.trim(),
        description: description.trim(),
        invite_code: code,
        leader_id: authState.user.id
      })
      .select()
      .single();

    if (dErr) throw dErr;

    await supabase.from('group_members').insert({
      group_id: directGroup.id,
      user_id: authState.user.id,
      role: 'leader'
    });

    await updateProfile({ cohort_name: directGroup.name });
    return directGroup;
  }

  await updateProfile({ cohort_name: data.name });
  return data;
}

export async function joinGroup(inviteCode) {
  if (!authState.user) throw new Error('You must be signed in to join a group.');
  const cleanCode = inviteCode.trim().toUpperCase();

  if (!isConfigured) {
    const existing = JSON.parse(localStorage.getItem(LOCAL_GROUPS_KEY) || '[]');
    const target = existing.find(g => g.invite_code.toUpperCase() === cleanCode);
    if (!target) throw new Error('Invalid group code. Please check with your group leader.');

    const memberGroup = { ...target, my_role: 'member' };
    localStorage.setItem(LOCAL_CURRENT_GROUP_KEY, JSON.stringify(memberGroup));
    await updateProfile({ cohort_name: target.name });
    return memberGroup;
  }

  const { data, error } = await supabase.rpc('join_group_by_code', {
    code: cleanCode
  });

  if (error) {
    // Fallback direct lookup
    const { data: targetGroup, error: findErr } = await supabase
      .from('groups')
      .select('*')
      .ilike('invite_code', cleanCode)
      .eq('is_active', true)
      .maybeSingle();

    if (findErr || !targetGroup) throw new Error('Invalid or inactive group code.');

    const { error: joinErr } = await supabase
      .from('group_members')
      .upsert({
        group_id: targetGroup.id,
        user_id: authState.user.id,
        role: 'member'
      });

    if (joinErr) throw joinErr;

    await updateProfile({ cohort_name: targetGroup.name });
    return targetGroup;
  }

  await updateProfile({ cohort_name: data.name });
  return data;
}

